/**
 * Simply Steaks - featured products slider.
 *
 * One product at a time; the others wait below and slide in as you scroll the
 * page (the big name travels further and scales more than the video, for a
 * layered look). The stage pins under the header for a short stretch - about
 * one screen per product - so scrolling steps through them, then the page
 * carries on. The videos are NOT scrubbed by scroll (see turntable.js: they
 * autoplay and rotate when touched).
 *
 * Escape hatches: the dots / up-down arrows / ArrowUp, ArrowDown, PageUp,
 * PageDown jump straight to a product by scrolling to it, and a "Skip" link
 * leaves the section. With prefers-reduced-motion nothing pins and those same
 * controls change the product directly.
 */
(function () {
  'use strict';

  if (window.__ssFeaturedSlider) {
    window.__ssFeaturedSlider.scan(document);
    return;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(max === undefined ? 1 : max, Math.max(min === undefined ? 0 : min, value));
  }

  function create(root) {
    var stage = root.querySelector('.featured-slider__stage');
    var panels = Array.prototype.slice.call(root.querySelectorAll('.fcard'));
    var names = panels.map(function (panel) {
      return panel.querySelector('.fcard__name');
    });
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slider-dot]'));
    var prev = root.querySelector('[data-slider-prev]');
    var next = root.querySelector('[data-slider-next]');
    var count = panels.length;
    if (!stage || count === 0) return null;

    var hold = parseFloat(root.getAttribute('data-hold'));
    var travel = parseFloat(root.getAttribute('data-travel'));
    hold = isFinite(hold) && hold >= 0 ? hold : 0.5;
    travel = isFinite(travel) && travel > 0 ? travel : 0.65;

    // Scroll layout in screen-heights: product i is still from starts[i] for
    // `hold`, then the slide to the next one takes `travel`.
    var starts = [];
    for (var s = 0; s < count; s += 1) starts.push(s * (hold + travel));
    var totalUnits = Math.max(count * hold + (count - 1) * travel, 0.01);

    var display = 0; // fractional product index currently shown
    var index = 0; // reduced-motion: the chosen product
    var rootTop = 0;
    var scrollLength = 1;
    var frame = 0;
    var near = false;
    var lastCurrent = -1;
    var lastActiveDot = -1;

    dots.forEach(function (dot, i) {
      if (names[i]) dot.setAttribute('aria-label', 'Show ' + names[i].textContent.trim());
    });
    panels.forEach(function (panel, i) {
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-roledescription', 'slide');
      panel.setAttribute('aria-label', i + 1 + ' of ' + count);
    });

    function locate(units) {
      for (var i = 0; i < count; i += 1) {
        var start = starts[i];
        if (units < start) return i - 1 + clamp((units - (start - travel)) / travel);
        if (units <= start + hold) return i;
      }
      return count - 1;
    }

    // The stage pins at `top: var(--header-h)`, so the pinned range starts when
    // the section top reaches that offset and ends when its bottom meets the
    // stage's bottom.
    function measure() {
      if (reduceMotion) return;
      var pageY = window.scrollY || window.pageYOffset;
      var rect = root.getBoundingClientRect();
      var stickyTop = parseFloat(window.getComputedStyle(stage).top) || 0;
      rootTop = rect.top + pageY - stickyTop;
      scrollLength = Math.max(rect.height - stage.offsetHeight, 1);
    }

    function scrollTargetFor(i) {
      // Middle of that product's still stretch.
      return rootTop + ((starts[i] + hold / 2) / totalUnits) * scrollLength;
    }

    function apply() {
      for (var i = 0; i < count; i += 1) {
        var panel = panels[i];
        var offset = display - i;
        var abs = Math.abs(offset);
        panel.style.transform = 'translateY(' + offset * 100 + '%) scale(' + (1 - Math.min(abs, 1) * 0.1).toFixed(4) + ')';
        panel.style.opacity = String(clamp(1 - abs));
        var hidden = abs >= 0.5;
        panel.style.pointerEvents = hidden ? 'none' : 'auto';
        if (panel.inert !== hidden) panel.inert = hidden;
        if (names[i]) {
          // The name travels further and pops bigger than the video beneath it.
          var nameScale = 1.08 - Math.min(abs, 1) * 0.36;
          names[i].style.transform = 'translateY(' + offset * 60 + '%) scale(' + nameScale.toFixed(3) + ')';
        }
        // The current product (and the one arriving) autoplays; the rest rest.
        if (abs < 0.75) {
          if (!panel.hasAttribute('data-current')) panel.setAttribute('data-current', '');
        } else if (panel.hasAttribute('data-current')) {
          panel.removeAttribute('data-current');
        }
      }

      var nearest = Math.round(display);
      if (nearest !== lastActiveDot) {
        lastActiveDot = nearest;
        dots.forEach(function (dot, i) {
          if (i === nearest) {
            dot.setAttribute('data-active', '');
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('data-active');
            dot.removeAttribute('aria-current');
          }
        });
        if (prev) prev.setAttribute('aria-disabled', nearest <= 0 ? 'true' : 'false');
        if (next) next.setAttribute('aria-disabled', nearest >= count - 1 ? 'true' : 'false');
      }
      if (nearest !== lastCurrent) {
        lastCurrent = nearest;
        document.dispatchEvent(new Event('ss:panel-change'));
      }
    }

    function tick() {
      frame = 0;
      var target;
      if (reduceMotion) {
        target = index;
        display = index;
      } else {
        var pageY = window.scrollY || window.pageYOffset;
        target = locate(clamp((pageY - rootTop) / scrollLength) * totalUnits);
        display += (target - display) * 0.14;
        if (Math.abs(target - display) < 0.0008) display = target;
      }
      apply();
      // Keep animating while it is catching up or the section is on screen.
      if (display !== target) frame = window.requestAnimationFrame(tick);
    }
    function wake() {
      if (!frame) frame = window.requestAnimationFrame(tick);
    }

    function goTo(target) {
      target = clamp(target, 0, count - 1);
      if (reduceMotion) {
        index = target;
        wake();
        return;
      }
      measure();
      window.scrollTo({ top: scrollTargetFor(target), behavior: 'smooth' });
    }
    function current() {
      return reduceMotion ? index : Math.round(display);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
      });
    });
    if (prev) {
      prev.addEventListener('click', function () {
        goTo(current() - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goTo(current() + 1);
      });
    }
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        if (current() < count - 1) {
          event.preventDefault();
          goTo(current() + 1);
        }
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        if (current() > 0) {
          event.preventDefault();
          goTo(current() - 1);
        }
      }
    });

    window.addEventListener('resize', function () {
      measure();
      wake();
    });
    window.addEventListener('load', function () {
      measure();
      wake();
    });
    window.addEventListener('scroll', wake, { passive: true });
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(
        function (entries) {
          near = entries[entries.length - 1].isIntersecting;
          measure();
          wake();
        },
        { rootMargin: '200px 0px 200px 0px' }
      ).observe(root);
    } else {
      near = true;
    }

    measure();
    apply();
    root.setAttribute('data-fslider-ready', '');
    wake();

    return {
      destroy: function () {
        if (frame) window.cancelAnimationFrame(frame);
      }
    };
  }

  function scan(context) {
    var roots = [];
    if (context.matches && context.matches('[data-fslider]')) roots.push(context);
    roots = roots.concat(Array.prototype.slice.call(context.querySelectorAll('[data-fslider]')));
    roots.forEach(function (root) {
      if (instances.has(root)) return;
      var instance = create(root);
      if (instance) instances.set(root, instance);
    });
  }

  window.__ssFeaturedSlider = { scan: scan };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll('[data-fslider]'));
    roots.forEach(function (root) {
      var instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });
})();
